import React, { useCallback, useState } from 'react';
import { useAnimation } from 'framer-motion';
import PropTypes from 'prop-types';
import Tooltip from 'components/Tooltip';
import history from 'routing/history';
import { last } from 'ramda';
import useImagesLoaded from 'hooks/useImagesLoaded';
import {
  NestContent,
  NestItemsWrapper,
  NestItem,
  EggShadow,
  EggImg,
  NestItemHeading,
  ModalButton,
  PopupModal
} from './styled';

const getItemText = (item) => {
  if (!item) {
    return;
  }
  if (!Array.isArray(item.text)) {
    return item.text;
  }
  return item.text[item.level] || last(item.text);
};

const NestItems = ({ eggs, setNestItemsLoaded }) => {
  const [popupItem, setPopupItem] = useState(null);
  const controls = useAnimation();

  const onImagesLoaded = useCallback(async () => {
    setNestItemsLoaded(true);
    await controls.start('finish');
  }, [controls, setNestItemsLoaded]);

  useImagesLoaded(
    eggs.map((nestItem) => nestItem.images[nestItem.level]),
    () => requestAnimationFrame(onImagesLoaded)
  );

  const popupItemLang = getItemText(popupItem);

  const parentVariants = {
    finish: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.05 }
    },
    start: {
      opacity: 0
    }
  };

  const childVariants = {
    finish: {
      y: 0,
      opacity: 1,
      transition: {
        y: {
          type: 'spring',
          damping: 300
        }
      }
    },
    start: {
      y: 20,
      opacity: 0
    }
  };

  return (
    <>
      <NestContent>
        <NestItemsWrapper variants={parentVariants} animate={controls} initial="start">
          {eggs.map((item, i) => {
            const text = getItemText(item);
            const isClickable = text.levelUpTip;

            return (
              <NestItem
                key={i}
                isClickable={isClickable}
                onClick={() => isClickable && setPopupItem(item)}
                variants={childVariants}
              >
                <EggImg
                  whileHover={{ y: -4 }}
                  whileTap={{ y: 0 }}
                  src={item.images[item.level]}
                  alt={text.title}
                />
                <EggShadow />
                <NestItemHeading>
                  <span>{text.title}</span>
                  {isClickable && <Tooltip size="small" />}
                </NestItemHeading>
              </NestItem>
            );
          })}
        </NestItemsWrapper>
      </NestContent>

      {popupItem && (
        <PopupModal
          isOpen={!!popupItem}
          onClose={() => setPopupItem(null)}
          header={`Grow your ${popupItemLang.title} bird`}
          color="dark"
        >
          {popupItemLang.levelUpTip}
          {popupItem.links &&
            popupItem.links.map((item, i) => (
              <ModalButton
                variant="primary"
                onClick={() => history.push(item.link)}
                disabled={item.disabled}
                key={i}
              >
                {item.linkTitle}
              </ModalButton>
            ))}
        </PopupModal>
      )}
    </>
  );
};

NestItems.propTypes = {
  eggs: PropTypes.array,
  setNestItemsLoaded: PropTypes.func
};

export default NestItems;
